import { ApiInstance, RetrySettings } from '@src/services/api-instance';
import { getAuthToken } from '@src/services/auth-service';
import {
  ConfigMapperDTO,
  CreateMappingsBodyDTO,
  UpdateMappingBodyDTO,
  OrganizationConfigDTO as OrgConfigGetResponseDTO,
  PutOrganizationConfigBodyDTO as OrgConfigUpsertBodyDTO,
} from './dto';
import type { OrgSetupCustomField } from '@src/services/org-config/dto';

export class AliareOrgSetupService {
  private readonly client: ApiInstance;

  constructor(client: ApiInstance) {
    this.client = client;
  }

  // Organization Config endpoints
  async getOrganizationConfig(organizationId: string, settings?: RetrySettings): Promise<OrgConfigGetResponseDTO> {
    return (
      await this.client.get<OrgConfigGetResponseDTO>(
        `/${organizationId}/config/`,
        {},
        {
          bearerToken: await getAuthToken(),
          settings,
        },
      )
    ).data;
  }

  async putOrganizationConfig(
    organizationId: string,
    body: OrgConfigUpsertBodyDTO,
    settings?: RetrySettings,
  ): Promise<OrgConfigGetResponseDTO> {
    return (
      await this.client.put<OrgConfigUpsertBodyDTO, OrgConfigGetResponseDTO>(`/${organizationId}/config/`, {}, body, {
        bearerToken: await getAuthToken(),
        settings,
      })
    ).data;
  }

  // Field Mapper endpoints
  async getAllMappings(organizationId: string, settings?: RetrySettings): Promise<ConfigMapperDTO[]> {
    return (
      await this.client.get<ConfigMapperDTO[]>(
        `/${organizationId}/mappings/`,
        {},
        {
          bearerToken: await getAuthToken(),
          settings,
        },
      )
    ).data;
  }

  async createMappings(organizationId: string, body: CreateMappingsBodyDTO, settings?: RetrySettings): Promise<void> {
    await this.client.post<CreateMappingsBodyDTO, void>(`/${organizationId}/mappings/`, body, {
      bearerToken: await getAuthToken(),
      settings,
    });
  }

  async replaceMappings(organizationId: string, body: CreateMappingsBodyDTO, settings?: RetrySettings): Promise<void> {
    await this.client.put<CreateMappingsBodyDTO, void>(`/${organizationId}/mappings/`, {}, body, {
      bearerToken: await getAuthToken(),
      settings,
    });
  }

  async updateMapping(
    organizationId: string,
    mappingId: string,
    body: UpdateMappingBodyDTO,
    settings?: RetrySettings,
  ): Promise<void> {
    await this.client.put<UpdateMappingBodyDTO, void>(`/${organizationId}/mappings/${mappingId}`, {}, body, {
      bearerToken: await getAuthToken(),
      settings,
    });
  }

  async getCustomFields(organizationId: string, settings?: RetrySettings): Promise<OrgSetupCustomField[]> {
    return (
      await this.client.get<OrgSetupCustomField[]>(
        `/${organizationId}/mappings/custom-fields`,
        {},
        {
          bearerToken: await getAuthToken(),
          settings,
        },
      )
    ).data;
  }
}
